import React from 'react';
import { Link } from 'react-router-dom';
import SitePage, { SectionHeader, Meander } from './SiteChrome';

// Pricing — every service with a public starting price. All numbers are
// anchors ("from $X"), scoped precisely after inquiry; edit them here, in one
// place. Care plans mirror the live config cost tiers (see the pricing
// analysis in the brain); the spec's "no public dollar amounts" was
// explicitly overridden by the client.

const CARE_PLANS = [
  {
    name: 'Essential',
    price: 99,
    line: 'The site, kept alive.',
    includes: [
      'Site + booking flow + admin, hosted and maintained',
      'SSL, backups, monitoring, and updates',
      'Small improvements every month',
      'Email support, two business days',
    ],
  },
  {
    name: 'Business',
    price: 249,
    line: 'For sites that do real work.',
    includes: [
      'Everything in Essential',
      'Client uploads and processing jobs',
      '100 GB media storage',
      'Priority support, next business day',
    ],
    featured: true,
  },
  {
    name: 'Voice',
    price: 449,
    line: 'A voice agent that answers.',
    includes: [
      'Everything in Business',
      'AI voice agent on your site and number',
      '500 voice minutes included',
      'Tuned to your services and booking flow',
    ],
  },
  {
    name: 'Scale',
    price: 899,
    line: 'High traffic, heavy compute.',
    includes: [
      'Everything in Voice',
      'CDN and burst compute for heavy jobs',
      '1,500 voice minutes included',
      'Same-day support, quarterly reviews',
    ],
  },
];

const PROJECTS = [
  { name: 'Identity system', from: 'from $2,000', line: 'Mark, palette, type, and the rules that hold them together.', interest: 'identity' },
  { name: 'Website', from: 'from $2,500', line: 'Designed and built as one piece — most land between $2.5k and $10k.', interest: 'web' },
  { name: 'Custom application', from: 'from $7,500', line: 'Booking engines, portals, dashboards — on the same foundation running this site.', interest: 'web' },
  { name: 'Photography', from: 'half-day from $900', line: 'Full day from $1,600. Real pictures of the real work.', interest: 'content' },
  { name: 'Presence engagement', from: 'from $1,200/mo', line: 'Six months of content, campaigns, and copy care with a defined cap.', interest: 'presence' },
];

const FAQ = [
  {
    q: 'What does "from" mean?',
    a: 'The floor, honestly stated. Every project is scoped and quoted after a conversation — the number only moves when the scope does, and you\u2019ll know before it does.',
  },
  {
    q: 'How does billing work?',
    a: 'Care plans bill monthly, flat, no meter-watching. Cancel or pause anytime with thirty days\u2019 notice — no lock-in, no exit fee. Projects are half on start, half on ship.',
  },
  {
    q: 'What happens if I cancel?',
    a: 'The work is yours. We hand over the code, the content, and the accounts, and help you land wherever you\u2019re going. Nothing is held hostage.',
  },
  {
    q: 'Who holds the ad budget?',
    a: 'You do, always — ad spend lives in your accounts, in your name. We design and run the campaigns; we never touch the wallet.',
  },
  {
    q: 'What if I go over the voice minutes?',
    a: 'Overage bills at a flat per-minute rate agreed up front, and we\u2019ll flag it before it happens — usually it means the agent is earning its keep and the next tier is cheaper.',
  },
];

export default function Pricing() {
  return (
    <SitePage>
      <section className="ac-section" style={{ paddingBottom: 0 }}>
        <div className="ac-lightwash" aria-hidden="true" />
        <div className="ac-wrap" style={{ position: 'relative' }}>
          <SectionHeader kicker="Pricing" title="Honest numbers, stated up front." />
          <p className="ac-reveal" style={{ maxWidth: '56ch', marginTop: -18, marginBottom: 8 }}>
            Everything we build runs on a monthly care plan — hosting, maintenance,
            and a studio that already knows your system. Projects are one-time and
            scoped after a conversation. All prices are starting points.
          </p>
        </div>
      </section>

      {/* Care plans */}
      <section className="ac-section" id="plans" style={{ paddingTop: 'clamp(36px, 5vw, 56px)' }}>
        <div className="ac-wrap">
          <span className="ac-kicker ac-reveal">Care &amp; hosting — monthly, flat, cancel anytime</span>
          <div className="ac-plans">
            {CARE_PLANS.map((p, i) => (
              <div className={`ac-plan ac-reveal ${p.featured ? 'featured' : ''}`} style={{ '--d': `${i * 0.09}s` }} key={p.name}>
                {p.featured && <span className="ac-plan-flag">Most common</span>}
                <h3>{p.name}<em>.</em></h3>
                <div className="ac-plan-price"><span>from</span> ${p.price}<span>/mo</span></div>
                <p className="ac-plan-line">{p.line}</p>
                <ul>
                  {p.includes.map((x) => <li key={x}>{x}</li>)}
                </ul>
                <Link className={`ac-btn ${p.featured ? '' : 'ac-btn-ghost'}`} to="/contact?interest=web">Start here</Link>
              </div>
            ))}
          </div>
        </div>
      </section>

      <Meander />

      {/* Projects */}
      <section className="ac-section" id="projects">
        <div className="ac-wrap">
          <SectionHeader kicker="Projects — one-time, scoped after inquiry" title="The build itself." />
          <div className="ac-projprice">
            {PROJECTS.map((p, i) => (
              <Link className="ac-projprice-row ac-reveal" style={{ '--d': `${i * 0.07}s` }} key={p.name} to={`/contact?interest=${p.interest}`}>
                <h3>{p.name}</h3>
                <span className="ac-projprice-line">{p.line}</span>
                <span className="ac-projprice-num">{p.from}</span>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Value framing */}
      <section className="ac-section ac-section-wash ac-quote-band">
        <span className="ac-amp-watermark" aria-hidden="true">&amp;</span>
        <div className="ac-wrap ac-reveal">
          <blockquote className="ac-quote" style={{ margin: 0 }}>
            A full-time designer-developer costs $6,000+ a month, and you still
            need the photographer and the copywriter. A studio on retainer costs
            less than the payroll tax — and it ships.
          </blockquote>
        </div>
      </section>

      {/* FAQ */}
      <section className="ac-section" id="faq">
        <div className="ac-wrap">
          <SectionHeader kicker="Questions" title="The fine print, out loud." />
          <div className="ac-faq">
            {FAQ.map((f, i) => (
              <details className="ac-faq-item ac-reveal" style={{ '--d': `${i * 0.06}s` }} key={f.q}>
                <summary>{f.q}<span aria-hidden="true">+</span></summary>
                <p>{f.a}</p>
              </details>
            ))}
          </div>
        </div>
      </section>
    </SitePage>
  );
}
